/**
 * Shared SVG marker for AutoGo vehicles on Google Maps.
 *
 * Renders a clean, top-down sedan inside a brand-colored pin with the licence
 * plate floating above. Designed to match AutoGo brand colors (#0a3042 navy +
 * #36e961 green) and to look nothing like Shrek :)
 */

const NAVY = '#0a3042';
const GREEN = '#36e961';
const WHITE = '#ffffff';

function escapeXml(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Build the AutoGo vehicle marker SVG (string).
 * @param {Object} opts
 * @param {string} [opts.plate] License plate to show in the small label.
 * @param {number} [opts.heading] Heading in degrees (0 = north). Rotates the car body only.
 * @param {boolean} [opts.showPlate=true]
 */
export function buildVehicleMarkerSvg({
  plate = '',
  heading = null,
  showPlate = true,
  highlighted = false,
  liveLabel = 'LIVE',
} = {}) {
  const safePlate = escapeXml((plate || '').toString().toUpperCase().slice(0, 10));
  const hasPlate = showPlate && safePlate.length > 0;
  const rot = Number.isFinite(Number(heading)) ? Number(heading) : 0;
  const safeLive = escapeXml(liveLabel);

  const liveBadge = highlighted
    ? `
        <g>
          <rect x="14" y="-18" width="36" height="16" rx="4" fill="#2563eb"/>
          <text x="32" y="-7" text-anchor="middle"
                fill="${WHITE}"
                font-family="Manrope, system-ui, sans-serif"
                font-size="9" font-weight="800" letter-spacing="0.8">${safeLive}</text>
        </g>
      `
    : '';

  // Plate label sits at the top, pin underneath.
  const plateBlock = hasPlate
    ? `
        <g>
          <rect x="6" y="0" width="52" height="20" rx="5" fill="${NAVY}"/>
          <rect x="6" y="0" width="52" height="20" rx="5" fill="none" stroke="${GREEN}" stroke-width="1.5"/>
          <text x="32" y="14" text-anchor="middle"
                fill="${WHITE}"
                font-family="Manrope, system-ui, -apple-system, Segoe UI, Roboto, sans-serif"
                font-size="11" font-weight="700" letter-spacing="0.5">${safePlate}</text>
        </g>
        <line x1="32" y1="20" x2="32" y2="24" stroke="${NAVY}" stroke-width="2" stroke-linecap="round"/>
      `
    : '';

  const yOffset = hasPlate ? 24 : 0;
  const topPad = highlighted ? 20 : 0;
  const totalH = (hasPlate ? 86 : 64) + topPad;
  const pinY = yOffset + topPad;
  const glowRing = highlighted
    ? `
        <circle cx="32" cy="${pinY + 30}" r="28" fill="${GREEN}" opacity="0.22"/>
        <circle cx="32" cy="${pinY + 30}" r="20" fill="none" stroke="${GREEN}" stroke-width="2.5" opacity="0.85"/>
      `
    : '';

  const pinPath = `M 32 ${pinY} C 18 ${pinY}, 8 ${pinY + 10}, 8 ${pinY + 28} C 8 ${pinY + 44}, 22 ${pinY + 56}, 32 ${pinY + 62} C 42 ${pinY + 56}, 56 ${pinY + 44}, 56 ${pinY + 28} C 56 ${pinY + 10}, 46 ${pinY}, 32 ${pinY} Z`;

  const viewMinY = highlighted ? -22 : 0;
  const viewHeight = totalH - viewMinY;

  return `
<svg xmlns="http://www.w3.org/2000/svg" width="64" height="${viewHeight}" viewBox="0 ${viewMinY} 64 ${viewHeight}">
  <defs>
    <filter id="agShadow" x="-20%" y="-20%" width="140%" height="140%">
      <feGaussianBlur in="SourceAlpha" stdDeviation="1.2"/>
      <feOffset dx="0" dy="1.5" result="off"/>
      <feComponentTransfer><feFuncA type="linear" slope="0.35"/></feComponentTransfer>
      <feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge>
    </filter>
  </defs>
  ${liveBadge}
  ${plateBlock}
  ${glowRing}
  <g filter="url(#agShadow)">
    <path d="${pinPath}" fill="${NAVY}"/>
    <path d="${pinPath}" fill="none" stroke="${GREEN}" stroke-width="${highlighted ? 2.5 : 1.75}"/>
  </g>
  <g transform="translate(32 ${pinY + 30}) rotate(${rot})">
    <!-- Top-down sedan: smooth body, windshields and headlights -->
    <path d="M -8.5,-13 C -8.5,-15.5 -6.5,-16.5 -3.5,-16.5 L 3.5,-16.5 C 6.5,-16.5 8.5,-15.5 8.5,-13 L 8.5,12.5 C 8.5,15 6.5,16.5 3.5,16.5 L -3.5,16.5 C -6.5,16.5 -8.5,15 -8.5,12.5 Z"
          fill="${GREEN}"/>
    <!-- Front windshield (faces heading direction) -->
    <path d="M -6,-12 L 6,-12 L 7,-5 L -7,-5 Z" fill="${NAVY}" opacity="0.92"/>
    <!-- Rear windshield -->
    <path d="M -7,5 L 7,5 L 6,11 L -6,11 Z" fill="${NAVY}" opacity="0.6"/>
    <!-- Headlights -->
    <rect x="-6.5" y="-15.5" width="3" height="1.6" rx="0.6" fill="${WHITE}"/>
    <rect x="3.5"  y="-15.5" width="3" height="1.6" rx="0.6" fill="${WHITE}"/>
    <!-- Tail lights -->
    <rect x="-6.5" y="14" width="3" height="1.4" rx="0.5" fill="#ff5252" opacity="0.85"/>
    <rect x="3.5"  y="14" width="3" height="1.4" rx="0.5" fill="#ff5252" opacity="0.85"/>
  </g>
</svg>`.trim();
}

/**
 * Build the Google Maps `icon` config for `new google.maps.Marker`.
 * Returns null if Google Maps SDK isn't loaded yet.
 */
export function buildVehicleMarkerIcon(opts = {}) {
  if (typeof window === 'undefined' || !window.google?.maps) return null;
  const svg = buildVehicleMarkerSvg(opts);
  const hasPlate = opts.showPlate !== false && Boolean(opts.plate);
  const highlighted = Boolean(opts.highlighted);
  const topPad = highlighted ? 20 : 0;
  const totalH = (hasPlate ? 86 : 64) + topPad;
  const scale = highlighted ? 1.15 : 1;
  const w = Math.round(64 * scale);
  const h = Math.round(totalH * scale);
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    scaledSize: new window.google.maps.Size(w, h),
    anchor: new window.google.maps.Point(Math.round(32 * scale), h),
  };
}

export const AUTOGO_BRAND_COLORS = { NAVY, GREEN, WHITE };
